import { zodResolver } from "@hookform/resolvers/zod";  
import { createUserWithEmailAndPassword, getAuth } from "firebase/auth";  
import { collection, doc, getDocs, getFirestore, setDoc } from "firebase/firestore";   
import { useEffect, useState } from 'react';  
import { useForm } from "react-hook-form";  
import { z } from "zod";  
import { Button } from "../../components/ui/button";  
import {  
  Card,  
  CardContent,  
  CardDescription,  
  CardFooter,  
  CardHeader,  
  CardTitle,  
} from "../../components/ui/card";  
import { Checkbox } from "../../components/ui/checkbox";  
import { Input } from "../../components/ui/input";  
import { Label } from "../../components/ui/label";  
import {  
  Select,  
  SelectContent,  
  SelectItem,  
  SelectTrigger,  
  SelectValue,  
} from "../../components/ui/select";  
import { app } from "../../firebase";  
import { useToast } from "../../hooks/use-toast";  
import { Clipboard } from "lucide-react";
import { useNavigate } from "react-router-dom";

// Validation schema  
const createUserSchema = z.object({  
  firstName: z.string().min(1, { message: "First Name is required." }),  
  lastName: z.string().min(1, { message: "Last Name is required." }),  
  email: z.string().email({ message: "Valid Email is required." }),  
  phone: z.string().min(10, { message: "Valid Phone number is required." }),  
  role: z.string().min(1, { message: "Role is required." }),  
  password: z.string().min(6, { message: "Password must be at least 6 characters long." }),  
  sendEmail: z.boolean().optional(),  
});  

const CreateUser = ({ user = null }) => {  
  const { toast } = useToast();  
  const [roles, setRoles] = useState([]);  
  const [isMasterAdmin, setIsMasterAdmin] = useState(user?.role === "MASTERADMIN");  
  
  // Form setup using react-hook-form  
  const form = useForm({  
    resolver: zodResolver(createUserSchema),  
    defaultValues: {  
      firstName: "",  
      lastName: "",  
      email: "",  
      phone: "",  
      role: "",  
      password: "",  
      sendEmail: true,  
    },  
  });  

  const { handleSubmit, control, setValue, setError, formState: { errors } } = form;  

  const db = getFirestore(app);  
  const auth = getAuth(app);  
  const navigate = useNavigate();

  // Fetch roles from the Firebase roles table  
  useEffect(() => {  
    const fetchRoles = async () => {  
      try {  
        const rolesCollection = collection(db, "roles");  
        const snapshot = await getDocs(rolesCollection);  
        const fetchedRoles = snapshot.docs.map((doc) => ({  
          id: doc.id,  
          ...doc.data(),  
        }));  
        const rolesArray = fetchedRoles  
          .filter((role:any) => isMasterAdmin || role.role !== "MASTERADMIN")  
          .map((role:any) => role.role)  
          .sort();  
        setRoles(rolesArray);  
      } catch (error) {  
        console.error("Error fetching roles:", error);  
      }  
    };  

    fetchRoles();  
  }, [db, isMasterAdmin]);  

  // Function to generate a temporary password  
  const generateTemporaryPassword = (email: string) => {  
    const basePassword = email.substring(0, 5).toLowerCase() + '@#$%^&321';  
    
    // If basePassword is less than 12 characters (which it will be), generate random characters to fill it up  
    const randomPartLength = 12 - basePassword.length;  
    const randomPart = Math.random().toString(36).substring(2, 2 + randomPartLength);  
    
    return basePassword + randomPart;  // Combine basePassword with random characters  
  };  

  // Handle form submission  
  const onSubmit = async (data) => {  
    try {  
      const userCredential = await createUserWithEmailAndPassword(auth, data.email, data.password);  
      const user = userCredential.user;  

      const userData = {  
        firstName: data.firstName,  
        lastName: data.lastName, 
        fullName:`${data.firstName} ${data.lastName}`, 
        emailId: data.email,  
        phone: data.phone,  
        role: data.role,  
        uid:user?.uid,
        createdAt: new Date(), // Optional: add a created timestamp  
        updatedAt: new Date(),
      };  
      const newUserRef = doc(collection(db, "users"));  
      await setDoc(newUserRef, { id: newUserRef.id, ...userData });  

      toast({  
        title: "User Created",  
        description: "User has been successfully created.",  
      });    

      form.reset();
      setTimeout(()=>{
        navigate("/admin/manage-access");  
      },1000)
      
    } catch (error) {  
      console.error("Error creating user:", error.message);  
      toast({  
        title: "Error",  
        description: error.message,  
        variant: "destructive",  
      });  
    }  
  };  

  // Handling email change to generate password  
  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {  
    const email = event.target.value;  
    setValue("email", email);  
    
    // Generate temporary password based on the email  
    if (email) {  
      const tempPassword = generateTemporaryPassword(email);  
      setValue("password", tempPassword);  
    }  
  };  

  // Copy password to clipboard  
  const copyPasswordToClipboard = async () => {  
    const password = form.getValues("password");  
    await navigator.clipboard.writeText(password);  
    toast({  
      title: "Password Copied",  
      description: "The temporary password has been copied to your clipboard.",  
    });  
  };  

  return (  
    <div className="container py-6 space-y-6">  
      <div className="flex justify-between items-center">  
        <h1 className="text-2xl font-bold text-center">Provide New LMS Access</h1>  
      </div>  

      <Card className="max-w-2xl mx-auto">  
      <CardHeader>
          <CardTitle>Issue LMS Access Credentials</CardTitle>
          <CardDescription>
            Provide access to the Learning Management System for a student
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">  
          <form onSubmit={handleSubmit(onSubmit)}>  
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">  
              <div className="space-y-2">  
                <Label htmlFor="firstName">First Name</Label>  
                <Input  
                  id="firstName"  
                  {...form.register("firstName")}  
                  placeholder="First name"  
                  className={errors.firstName ? "border-red-500" : ""}  
                />  
                {errors.firstName && <p className="text-red-500 text-sm">{errors.firstName.message}</p>}  
              </div>  
              <div className="space-y-2">  
                <Label htmlFor="lastName">Last Name</Label>  
                <Input  
                  id="lastName"  
                  {...form.register("lastName")}  
                  placeholder="Last name"  
                  className={errors.lastName ? "border-red-500" : ""}  
                />  
                {errors.lastName && <p className="text-red-500 text-sm">{errors.lastName.message}</p>}  
              </div>  
            </div>  

            <div className="space-y-2">  
              <Label htmlFor="email">Email</Label>  
              <Input  
                id="email"  
                type="email"  
                {...form.register("email", { onChange: handleEmailChange })}  // Attach the handleEmailChange function  
                placeholder="Email address"  
                className={errors.email ? "border-red-500" : ""}  
              />  
              {errors.email && <p className="text-red-500 text-sm">{errors.email.message}</p>}  
            </div>  

            <div className="space-y-2">  
              <Label htmlFor="phone">Phone</Label>  
              <Input  
                id="phone"  
                type="tel"  
                {...form.register("phone")}  
                placeholder="Phone number"  
                className={errors.phone ? "border-red-500" : ""}  
              />  
              {errors.phone && <p className="text-red-500 text-sm">{errors.phone.message}</p>}  
            </div>  

            <div className="space-y-2">  
              <Label htmlFor="role">Role</Label>  
              <Select  
                onValueChange={(value) => {   
                  setValue("role", value);  
                  if (errors.role) {  
                    setError("role", { message: "" });  
                  }  
                }}  
              >  
                <SelectTrigger>  
                  <SelectValue placeholder="Select role" />  
                </SelectTrigger>  
                <SelectContent>  
                  {roles.map((role, index) => (  
                    <SelectItem key={index} value={role}>  
                      {role}  
                    </SelectItem>  
                  ))}  
                </SelectContent>  
              </Select>  
              {errors.role && <p className="text-red-500 text-sm">{errors.role.message}</p>}  
            </div>  

            <div className="space-y-2">  
              <Label htmlFor="password">Temporary Password</Label>  
              <div className="flex items-center space-x-2">  
                <Input  
                  id="password"  
                  type="password"  
                  {...form.register("password")}  
                  placeholder="Temporary password"  
                  className={errors.password ? "border-red-500" : ""}  
                />  
                <Button type="button" onClick={copyPasswordToClipboard}><Clipboard /> </Button>  
              </div>  
              {errors.password && <p className="text-red-500 text-sm">{errors.password.message}</p>}  
            </div>  

            <div className="flex items-start space-x-2 pt-2">  
              <Checkbox  
                id="send-email"  
                {...form.register("sendEmail")}  
                checked={form.getValues("sendEmail")}   
              />  
              <div className="grid gap-1.5 leading-none">  
                <p className="text-sm text-gray-500">  
                  User will receive login details in their email  
                </p>  
              </div>  
            </div>  

            <CardFooter className="flex justify-between py-8 px-0">  
              <Button variant="outline" type="button" onClick={() =>navigate("/lms/waiting-list")}>  
               
                Cancel  
              </Button>  
              <Button type="submit">Create User</Button>  
            </CardFooter>  
          </form>  
        </CardContent>  
      </Card>  
    </div>  
  );  
};  

export default CreateUser;  